import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaBars, FaTimes, FaSignOutAlt, FaTachometerAlt, FaClipboardList, FaGift, FaChevronDown, FaHistory } from 'react-icons/fa';

const ResponsiveNavbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setProfileDropdownOpen(false);
      }
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false);
    setProfileDropdownOpen(false);
  }, [location.pathname]);

  // Handle keyboard navigation
  const handleKeyDown = (event, action) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
    if (event.key === 'Escape') {
      setIsOpen(false);
      setProfileDropdownOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    setProfileDropdownOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const toggleMobileMenu = () => {
    setIsOpen(!isOpen);
    setProfileDropdownOpen(false);
  };

  const toggleProfileDropdown = () => {
    setProfileDropdownOpen(!profileDropdownOpen);
  };

  return (
    <nav className="bg-primary-900 sticky top-0 z-50 shadow-lg" role="navigation" aria-label="Main navigation">
      <div className="container-responsive">
        <div className="flex justify-between items-center py-3 sm:py-4">
          {/* Logo */}
          <Link
            to={user ? '/dashboard' : '#'}
            className={`flex items-center bg-white bg-opacity-20 px-3 sm:px-4 py-2 rounded-lg transition-all duration-300 shadow-md focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
              user
                ? 'hover:bg-gray-100 transform hover:-translate-y-0.5'
                : 'cursor-not-allowed'
            }`}
            aria-label="Cred Encuesta - Go to homepage"
            aria-disabled={!user}
            onClick={(e) => !user && e.preventDefault()}
          >
            <img
              src="/cred-encuesta-logo.png" 
              alt="Cred Encuesta Logo" 
              className="h-10 sm:h-12 w-auto"
              style={{filter: 'brightness(0) invert(1)'}} 
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm xl:text-base focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/dashboard') 
                      ? 'bg-white bg-opacity-20 text-white shadow-md' 
                      : 'text-gray-200 hover:text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                  aria-current={isActive('/dashboard') ? 'page' : undefined}
                >
                  <FaTachometerAlt aria-hidden="true" />
                  <span>Dashboard</span>
                </Link>
                <Link 
                  to="/surveys" 
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm xl:text-base focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/surveys') 
                      ? 'bg-white bg-opacity-20 text-white shadow-md' 
                      : 'text-gray-200 hover:text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                  aria-current={isActive('/surveys') ? 'page' : undefined}
                >
                  <FaClipboardList aria-hidden="true" />
                  <span>Surveys</span>
                </Link>
                <Link 
                  to="/rewards" 
                  className={`flex items-center space-x-2 px-3 xl:px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm xl:text-base focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/rewards') 
                      ? 'bg-white bg-opacity-20 text-white shadow-md' 
                      : 'text-gray-200 hover:text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                  aria-current={isActive('/rewards') ? 'page' : undefined}
                >
                  <FaGift aria-hidden="true" />
                  <span>Rewards</span>
                </Link>
                
                {/* Profile Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button 
                    onClick={toggleProfileDropdown}
                    onKeyDown={(e) => handleKeyDown(e, toggleProfileDropdown)}
                    className="flex items-center space-x-2 text-gray-200 hover:text-white transition-colors duration-200 px-3 py-2 rounded-lg hover:bg-white hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                    aria-expanded={profileDropdownOpen}
                    aria-haspopup="true"
                    aria-label={`User menu for ${user.firstName}`}
                  >
                    <FaUser aria-hidden="true" />
                    <span className="hidden xl:inline text-sm xl:text-base">{user.firstName}</span>
                    <FaChevronDown 
                      className={`transition-transform duration-200 ${profileDropdownOpen ? 'rotate-180' : ''}`} 
                      aria-hidden="true"
                    />
                  </button>
                  
                  {profileDropdownOpen && (
                    <div 
                      className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50"
                      role="menu"
                      aria-orientation="vertical"
                    >
                      <Link 
                        to="/profile" 
                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
                        onClick={() => setProfileDropdownOpen(false)}
                        role="menuitem"
                      >
                        <FaUser className="text-primary-600" aria-hidden="true" />
                        <span className="font-medium">Profile</span>
                      </Link>
                      <Link 
                        to="/payment-history" 
                        className="flex items-center space-x-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors duration-200 focus:outline-none focus:bg-gray-50"
                        onClick={() => setProfileDropdownOpen(false)}
                        role="menuitem"
                      >
                        <FaHistory className="text-primary-600" aria-hidden="true" />
                        <span className="font-medium">Payment History</span>
                      </Link>
                      <hr className="my-1 border-gray-200" role="separator" />
                      <button 
                        onClick={handleLogout}
                        className="flex items-center space-x-3 px-4 py-3 text-red-600 hover:bg-red-50 transition-colors duration-200 w-full text-left focus:outline-none focus:bg-red-50"
                        role="menuitem"
                      >
                        <FaSignOutAlt aria-hidden="true" />
                        <span className="font-medium">Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3 xl:space-x-4">
                <Link 
                  to="/login" 
                  className={`px-3 xl:px-4 py-2 rounded-lg transition-all duration-200 font-medium text-sm xl:text-base focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/login') 
                      ? 'bg-white text-primary-900 shadow-md' 
                      : 'text-gray-200 hover:text-white hover:bg-white hover:bg-opacity-10'
                  }`}
                  aria-current={isActive('/login') ? 'page' : undefined}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className={`px-4 xl:px-6 py-2 rounded-lg font-semibold transition-all duration-200 transform hover:scale-105 text-sm xl:text-base focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/register') 
                      ? 'bg-white text-primary-900 shadow-md' 
                      : 'text-gray-200 hover:text-white hover:bg-white hover:bg-opacity-10 border border-gray-300'
                  }`}
                  aria-current={isActive('/register') ? 'page' : undefined}
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button 
            onClick={toggleMobileMenu}
            onKeyDown={(e) => handleKeyDown(e, toggleMobileMenu)}
            className="lg:hidden text-white hover:text-gray-200 transition-colors duration-200 p-2 rounded-lg hover:bg-white hover:bg-opacity-10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
            aria-expanded={isOpen}
            aria-controls="mobile-menu"
            aria-label={isOpen ? 'Close menu' : 'Open menu'}
          >
            {isOpen ? <FaTimes size={20} aria-hidden="true" /> : <FaBars size={20} aria-hidden="true" />}
          </button>
        </div>

        {/* Mobile Navigation */}
        <div 
          id="mobile-menu"
          className={`lg:hidden transition-all duration-300 ease-in-out overflow-hidden ${
            isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          }`}
          ref={mobileMenuRef}
          role="menu"
          aria-orientation="vertical"
        >
          <div className="bg-primary-800 rounded-lg mt-2 py-4 space-y-1">
            {user ? (
              <>
                <Link 
                  to="/dashboard" 
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mx-2 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/dashboard') 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : 'text-gray-200 hover:text-white hover:bg-primary-700'
                  }`}
                  role="menuitem"
                  aria-current={isActive('/dashboard') ? 'page' : undefined}
                >
                  <FaTachometerAlt aria-hidden="true" />
                  <span>Dashboard</span>
                </Link>
                <Link 
                  to="/surveys" 
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mx-2 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/surveys') 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : 'text-gray-200 hover:text-white hover:bg-primary-700'
                  }`}
                  role="menuitem"
                  aria-current={isActive('/surveys') ? 'page' : undefined}
                >
                  <FaClipboardList aria-hidden="true" />
                  <span>Surveys</span>
                </Link>
                <Link 
                  to="/rewards" 
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg mx-2 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/rewards') 
                      ? 'bg-white bg-opacity-20 text-white' 
                      : 'text-gray-200 hover:text-white hover:bg-primary-700'
                  }`}
                  role="menuitem"
                  aria-current={isActive('/rewards') ? 'page' : undefined}
                >
                  <FaGift aria-hidden="true" />
                  <span>Rewards</span>
                </Link>
                <hr className="border-primary-700 mx-2 my-2" role="separator" />
                <Link 
                  to="/profile" 
                  className="flex items-center space-x-3 px-4 py-3 text-gray-200 hover:text-white hover:bg-primary-700 rounded-lg mx-2 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                  role="menuitem"
                >
                  <FaUser aria-hidden="true" />
                  <span>Profile</span>
                </Link>
                <Link 
                  to="/payment-history" 
                  className="flex items-center space-x-3 px-4 py-3 text-gray-200 hover:text-white hover:bg-primary-700 rounded-lg mx-2 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                  role="menuitem"
                >
                  <FaHistory aria-hidden="true" />
                  <span>Payment History</span>
                </Link>
                <button 
                  onClick={handleLogout}
                  className="flex items-center space-x-3 px-4 py-3 text-red-300 hover:text-red-200 hover:bg-red-600 rounded-lg mx-2 w-full text-left transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
                  role="menuitem"
                >
                  <FaSignOutAlt aria-hidden="true" />
                  <span>Logout</span>
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className={`block px-4 py-3 rounded-lg mx-2 transition-all duration-200 font-medium focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/login') 
                      ? 'bg-white text-primary-900' 
                      : 'text-gray-200 hover:text-white hover:bg-primary-700'
                  }`}
                  role="menuitem"
                  aria-current={isActive('/login') ? 'page' : undefined}
                >
                  Login
                </Link>
                <Link 
                  to="/register" 
                  className={`block px-4 py-3 rounded-lg mx-2 font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 ${
                    isActive('/register') 
                      ? 'bg-white text-primary-900' 
                      : 'text-gray-200 hover:text-white hover:bg-primary-700 border border-gray-400'
                  }`}
                  role="menuitem"
                  aria-current={isActive('/register') ? 'page' : undefined}
                >
                  Sign Up
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default ResponsiveNavbar;